import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  sendGetClientRequest,
  sendPostClientRequest,
  sendDeleteClientRequest,
  sendPatchClientRequest,
} from "../../../api/helper";

import { serialize } from "../../../utils";

export const getPlanPackages = createAsyncThunk(
  "plans/getPlanPackages",
  async (service) => {
    const response = await sendGetClientRequest(
      `/api/software-services/${service}/plan-packages`
    );
    return response.data;
  }
);

// 取得方案組合列表(新) - 未啟用(publishing = 0)
export const getPlanPackagesNew = createAsyncThunk(
  "plans/getPlanPackagesNew",
  async (parameters) => {
    const queryObj = {};
    if (parameters) {
      if (parameters.limit) queryObj.rows = parameters.limit;
      if (parameters.page) queryObj.page = parameters.page;
      if (parameters.search) queryObj.search = parameters.search;
      // 方案種類：一般方案(group_by_plan) / 儲值金(stored_value)。未指定時維持原本的一般方案。
      queryObj.type = parameters.type || "group_by_plan";
    }

    let queryString = "";
    if (JSON.stringify(queryObj) !== "{}")
      queryString = "?publishing=0&" + serialize(queryObj);

    // const response = await sendGetClientRequest(`/api/software-services/${parameters.service}/grouped-plan-packages${queryString}`);
    const response = await sendGetClientRequest(
      `/api/software-services/${parameters.service}/plan-packages${queryString}`
    );
    return response.data;
  }
);

// 取得方案組合列表(新) - 已啟用(publishing = 1)
export const getPlanPackagesPublishing = createAsyncThunk(
  "plans/getPlanPackagesPublishing",
  async (parameters) => {
    // const response = await sendGetClientRequest(`/api/software-services/${parameters.service}/grouped-plan-packages?publishing=1`);
    const type = parameters.type || "group_by_plan";
    const response = await sendGetClientRequest(
      `/api/software-services/${parameters.service}/plan-packages?publishing=1&type=${type}`
    );
    return response.data;
  }
);

export const getPlanPackageDetail = createAsyncThunk(
  "plans/getPlanPackageDetail",
  async (id) => {
    const response = await sendGetClientRequest(
      `/api/software-service-plan-packages/${id}`
    );
    return response.data;
  }
);

export const getPlanPackageDetailNew = createAsyncThunk(
  "plans/getPlanPackageDetailNew",
  async (id) => {
    const response = await sendGetClientRequest(
      `/api/software-service-grouped-plan-packages/${id}`
    );
    return response.data;
  }
);

export const createPlanPackage = createAsyncThunk(
  "plans/createPlanPackage",
  async (parameters) => {
    const postObj = {
      name: parameters.name,
      plans: parameters.plans,
    };
    const response = await sendPostClientRequest(
      `/api/software-services/${parameters.service}/plan-packages`,
      postObj
    );
    return response;
  }
);

export const createPlanPackageNew = createAsyncThunk(
  "plans/createPlanPackageNew",
  async (parameters, { rejectWithValue }) => {
    const response = await sendPostClientRequest(
      `/api/software-services/${parameters.service}/plan-packages`,
      parameters
    );
    // sendApiClientRequest 對 4xx 不 throw，而是回傳 response.data；需在此判斷失敗。
    if (response?.error || response?.msg !== "success") {
      return rejectWithValue(response);
    }
    return response;
  }
);

export const deletePlanPackage = createAsyncThunk(
  "plans/deletePlanPackage",
  async (id) => {
    const response = await sendDeleteClientRequest(
      `/api/software-service-plan-packages/${id}`
    );
    return response;
  }
);

export const activatePlanPackage = createAsyncThunk(
  "plans/activatePlanPackage",
  async (id) => {
    const response = await sendPostClientRequest(
      `/api/software-service-plan-packages/${id}/publish`
    );
    return response;
  }
);

export const publishPlanPackage = createAsyncThunk(
  "plans/publishPlanPackage",
  async (parameters) => {
    const response = await sendPatchClientRequest(
      `/api/software-service-plan-packages/${parameters.id}`,
      parameters
      // { publish_month: parameters.publish_month }
    );
    return response;
  }
);

// 強制停用目前生效中的方案組合（後端會清掉 publishing / publish_at，回到未啟用草稿）。
export const unpublishPlanPackage = createAsyncThunk(
  "plans/unpublishPlanPackage",
  async (id) => {
    const response = await sendPostClientRequest(
      `/api/software-service-plan-packages/${id}/unpublish`
    );
    return response;
  }
);

const planSlice = createSlice({
  name: "plans",
  initialState: {
    packages: [],
    package_detail: null,
    success: false,
    refresh: false,
    loading: false,
    publish: false,
    publishError: false,
    // 新增方案失敗（如啟用區間重疊）時存後端訊息，供 Toast 以錯誤樣式顯示。
    createError: false,
    newPlanLoading: false,
    packagesServiceId: null,
    // 記錄目前 packages 屬於哪個方案種類，
    // 切換訂閱／席位時，資料型別未對上前先進 loading，避免顯示上一個型別的殘留資料。
    packagesType: null,
    // 依方案種類存放「啟用中」方案：{ group_by_plan: {...}, stored_value: {...} }。
    // 供「訂閱與席位擇一」跨類型偵測用（同一服務同時只能有一個生效方案，不分類型）。
    publishingByType: {},
    publishingByTypeServiceId: null,
  },
  reducers: {
    updateDetailPlan: (state) => {
      state.package_detail = null;
    },
    removeSuccess: (state) => {
      state.refresh = false;
      state.publish = false;
    },
    removePublishError: (state) => {
      state.publishError = false;
    },
    removeCreateError: (state) => {
      state.createError = false;
    },
  },
  extraReducers: {
    // GET PLAN PACKAGES
    [getPlanPackages.pending]: (state) => {
      state.loading = true;
    },
    [getPlanPackages.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.packages = payload;
        state.loading = false;
      } else {
        state.loading = false;
      }
    },
    [getPlanPackages.rejected]: (state) => {
      state.loading = false;
    },

    // GET PLAN PACKAGES New
    [getPlanPackagesNew.pending]: (state) => {
      state.loading = true;
    },
    [getPlanPackagesNew.fulfilled]: (state, { payload, meta }) => {
      if (payload) {
        state.packages = payload;
        // 記錄這批資料屬於哪個 service，切換 service 時才能判斷是否為前一個 service 的殘留資料。
        state.packagesServiceId = meta?.arg?.service ?? null;
        state.packagesType = meta?.arg?.type ?? 'group_by_plan';
        state.loading = false;
      } else {
        state.loading = false;
      }
    },
    [getPlanPackagesNew.rejected]: (state) => {
      state.loading = false;
    },

    [getPlanPackagesPublishing.pending]: (state) => {
      state.loading = true;
    },
    [getPlanPackagesPublishing.fulfilled]: (state, { payload, meta }) => {
      if (payload) {
        const type = meta?.arg?.type ?? 'group_by_plan';
        const service = meta?.arg?.service ?? null;
        // 切換服務時清掉上一個服務殘留的各類型啟用資料，避免跨類型偵測用到舊 service 的資料。
        if (String(state.publishingByTypeServiceId) !== String(service)) {
          state.publishingByType = {};
          state.publishingByTypeServiceId = service;
        }
        state.publishingByType[type] = payload;
        state.loading = false;
      } else {
        state.loading = false;
      }
    },
    [getPlanPackagesPublishing.rejected]: (state) => {
      state.loading = false;
    },

    // GET PLAN PACKAGES DETAIL
    [getPlanPackageDetail.pending]: (state) => {
      state.loading = true;
    },
    [getPlanPackageDetail.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.package_detail = payload;
        state.loading = false;
      }
    },
    [getPlanPackageDetail.rejected]: (state) => {
      state.loading = false;
    },

    // GET PLAN PACKAGES DETAIL NEW
    [getPlanPackageDetailNew.pending]: (state) => {
      state.loading = true;
    },
    [getPlanPackageDetailNew.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.package_detail = payload;
        state.loading = false;
      }
    },
    [getPlanPackageDetailNew.rejected]: (state) => {
      state.loading = false;
    },

    // CREATE PLAN PACKAGES
    [createPlanPackage.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },
    [createPlanPackage.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.success = "新增方案組合成功";
        state.loading = false;
      }
    },
    [createPlanPackage.rejected]: (state) => {
      state.loading = false;
    },

    // CREATE PLAN PACKAGES NEW
    [createPlanPackageNew.pending]: (state) => {
      state.refresh = false;
      state.newPlanLoading = true;
    },
    [createPlanPackageNew.fulfilled]: (state, { payload }) => {
      state.newPlanLoading = false;
      if (payload?.msg === "success") {
        state.refresh = true;
      }
    },
    [createPlanPackageNew.rejected]: (state, { payload }) => {
      state.newPlanLoading = false;
      state.refresh = false;
      // 帶回後端訊息（如「同一服務不可有啟用區間重疊的方案…」）給 Toast。
      state.createError = payload?.msg || "新增方案組合失敗";
    },

    // DELETE PLAN PACKAGES
    [deletePlanPackage.pending]: (state) => {
      state.loading = true;
      state.refresh = false;
    },
    [deletePlanPackage.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.refresh = true;
        state.loading = false;
      }
    },
    [deletePlanPackage.rejected]: (state) => {
      // state.loading = false;
      state.refresh = false;
    },

    // ACTIVATE PLAN PACKAGES
    [activatePlanPackage.pending]: (state) => {
      state.loading = true;
      state.success = false;
    },
    [activatePlanPackage.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.success = "啟用成功";
        state.loading = false;
      }
    },
    [activatePlanPackage.rejected]: (state) => {
      state.loading = false;
    },

    // PUBLISH PLAN PACKAGES
    [publishPlanPackage.pending]: (state) => {
      state.loading = true;
    },
    [publishPlanPackage.fulfilled]: (state, { payload }) => {
      if (payload) {
        if (payload.msg === "success") {
          state.publish = true;
          state.loading = false;
        } else {
          state.publishError = true;
          state.loading = false;
        }
      }
    },
    [publishPlanPackage.rejected]: (state) => {
      state.loading = false;
    },

    // UNPUBLISH (強制停用) PLAN PACKAGES
    [unpublishPlanPackage.pending]: (state) => {
      state.loading = true;
      state.refresh = false;
    },
    [unpublishPlanPackage.fulfilled]: (state, { payload }) => {
      if (payload?.msg === "success") {
        state.refresh = true;
        state.loading = false;
      } else {
        state.loading = false;
      }
    },
    [unpublishPlanPackage.rejected]: (state) => {
      state.loading = false;
      state.refresh = false;
    },
  },
});

export default planSlice.reducer;
export const selectPlans = (state) => state.plans;
export const {
  updateDetailPlan,
  removeSuccess,
  removePublishError,
  removeCreateError,
} = planSlice.actions;
